/**
 * The construction stack: levels, the layers between them, and the roll-up.
 *
 * The TypeScript twin of `scripts/layer_stack.py`, which is the source of
 * truth. A LEVEL is a surface (top of binder, formation). A LAYER is the
 * material between two adjacent levels. N levels describe N-1 layers — five
 * materials need six levels.
 *
 * `layer_state` is deliberately NOT ported (ruling R10). Every client consumer
 * reads `layers[i].state` off the RESULT DOCUMENT the worker produced;
 * nothing calls a client-side `layerState`. Porting it would create a second
 * source of truth for the PENDING rule, in a second language, with no caller
 * to keep it honest. If a later task genuinely needs a live preview before a
 * run has happened, it adds it THEN — with a caller, a test and a scraped pin.
 */

/**
 * Worst-first. `INCONCLUSIVE` outranks `FAIL` because a result you could not
 * measure cannot be acted on, which is worse than one you measured and failed.
 * `PENDING` is NOT in this order — it is excluded from a roll-up entirely.
 *
 * Pinned character-for-character to `layer_stack.py`'s tuple by
 * `__tests__/model.test.ts`, which scrapes the Python source. Do not hand-edit
 * one side.
 */
export const SEVERITY_ORDER = ['INCONCLUSIVE', 'FAIL', 'MARGINAL', 'PASS'] as const;

export type Severity = (typeof SEVERITY_ORDER)[number];

/** Anything a roll-up may legitimately be handed, including the absences. */
export type RollUpInput = Severity | 'PENDING' | null | undefined;

/** Adjacent index pairs. N levels → N-1 layers. */
export function layersOf<T>(levels: readonly T[]): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < levels.length - 1; i += 1) out.push([i, i + 1]);
  return out;
}

/**
 * Worst severity across the stack. `PENDING` is excluded, never a pass.
 *
 * A structure with one failing layer and three not-yet-built ones reads FAIL.
 * A structure where nothing is built yet reads PENDING — reporting PASS for an
 * unbuilt pavement is the single worst thing this could do.
 *
 * **Throws on a verdict it does not recognise**, exactly as the Python does.
 * Silently returning `PENDING` there would report a layer that WAS built and
 * DID produce a verdict as not-yet-built, and PENDING is then excluded from
 * the roll-up — the divergence a happy-path suite cannot see.
 */
export function rollUp(verdicts: readonly RollUpInput[]): Severity | 'PENDING' {
  const real = verdicts.filter((v): v is Severity => Boolean(v) && v !== 'PENDING');
  if (real.length === 0) return 'PENDING';

  const unknown = real.filter((v) => !(SEVERITY_ORDER as readonly string[]).includes(v));
  if (unknown.length > 0) {
    throw new Error(
      `unknown verdict(s) ${JSON.stringify(unknown)}; expected one of ${SEVERITY_ORDER.join(', ')}`,
    );
  }

  for (const sev of SEVERITY_ORDER) if (real.includes(sev)) return sev;
  // Unreachable: `real` is non-empty and every member is in SEVERITY_ORDER.
  throw new Error('unreachable: no severity matched a non-empty, validated set');
}
